import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import WizardProgress from '../components/WizardProgress';
import { buildReferralLink } from '../utils/referral';
import { copyToClipboard } from '../utils/clipboard';

// Admin-facing first implementation of jc/wizard_plan.md's signup wizard.
// Account creation + package funding reuse the existing, already-tested
// signup-internal backend (operator funds the new member's package from
// their own Credit/RnR balance) -- this build is the wizard UI/UX
// rebuild + the plan's confirmed backend correctness fixes (password
// strength enforced server-side, validate-affiliate schema fix), not a
// rewrite of the funding mechanism itself. The public self-service
// /signup replacement (with the Google branch and the sales-team
// RnR-transfer-to-recipient funding option) is a later fast-follow per
// the plan.

const STEP_LABELS_EN = ['Referral', 'Identity', 'Contact & Password', 'Package & Funding'];
const STEP_LABELS_ZH = ['推荐人', '身份信息', '联系方式与密码', '套餐与资金'];

function validatePasswordStrength(pwd) {
  if (!pwd || pwd.length < 8) return 'weak';
  const hasLower = /[a-z]/.test(pwd);
  const hasUpper = /[A-Z]/.test(pwd);
  const hasNumber = /\d/.test(pwd);
  const hasSpecial = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?-]/.test(pwd);
  const score = [hasLower, hasUpper, hasNumber, hasSpecial].filter(Boolean).length;
  if (score < 2) return 'weak';
  if (score === 2) return 'fair';
  if (score === 3) return 'good';
  return 'strong';
}

export default function SignupWizard({ mode = 'internal' }) {
  const isPublic = mode === 'public';
  const { user, token: adminToken, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const isAdmin = user && ['admin', 'superadmin', 'sales-team'].includes(user.role);

  // Internal mode: admin funds from their own Credit/RnR balance (existing,
  // unchanged) or generates a card-payment link for the new member (new).
  // Public mode: defaults to card payment -- a new visitor has no Credit/RnR
  // balance of their own -- but can switch to PayNow or (if their sponsor is
  // sales-team) Request from Upline.
  const [fundingMethod, setFundingMethod] = useState(isPublic ? 'stripe' : 'credit_rnr');
  const [stripeSubmitting, setStripeSubmitting] = useState(false);
  const [stripeRedirect, setStripeRedirect] = useState(null); // { wellisId, url } -- shown before leaving the page

  // Public mode only: PayNow (SGD, via Stripe) -- separate from the card
  // (USD) Stripe flow above, not a variant of it. Same
  // paid_awaiting_admin_confirmation admin-review pattern.
  const [paynowSubmitting, setPaynowSubmitting] = useState(false);

  // Public mode only: when the detected sponsor (Step 1) is a
  // sales-team member, Step 4 offers "Request from upline" as an
  // alternative to Stripe -- create the account normally, then submit a
  // package_funding_requests row naming that sponsor instead of paying.
  const [uplineRequestSubmitting, setUplineRequestSubmitting] = useState(false);

  const [activeTab, setActiveTab] = useState('wizard'); // 'wizard' | 'share'
  const [copyStatus, setCopyStatus] = useState('');

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');

  // Step 1 -- Referral
  const [referralCode, setReferralCode] = useState('');
  const [affiliateName, setAffiliateName] = useState('');
  const [affiliateRole, setAffiliateRole] = useState('');
  const [affiliateError, setAffiliateError] = useState('');
  const [validatingAffiliate, setValidatingAffiliate] = useState(false);

  // Step 2 -- Identity
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [wellisId, setWellisId] = useState('');
  const [wellisIdAvailable, setWellisIdAvailable] = useState(null);
  const [wellisIdError, setWellisIdError] = useState('');
  const [checkingWellisId, setCheckingWellisId] = useState(false);

  // Step 3 -- Contact & credentials
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 4 -- Package & funding
  const [packages, setPackages] = useState([]);
  const [packageId, setPackageId] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [rnrBalance, setRnrBalance] = useState(0);
  const [conversionRate, setConversionRate] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  // Public mode: mandatory acknowledgement of the refund policy before any
  // payment path can proceed.
  const [agreedToRefundPolicy, setAgreedToRefundPolicy] = useState(false);

  // Payment proof upload (Step 4 success screen)
  const [proofFiles, setProofFiles] = useState([]);
  const [proofUploaded, setProofUploaded] = useState([]);
  const [proofError, setProofError] = useState('');
  const [uploadingProof, setUploadingProof] = useState(false);
  const MAX_PROOF_PHOTOS = 5;
  const MAX_PROOF_BYTES = 5 * 1024 * 1024;

  // Public mode: after Stripe redirects back from a PayNow checkout
  // (?stripe=success&order_id=...), the account + session already exist
  // (token was persisted to localStorage before the redirect) -- show the
  // same proof-upload UI as the internal-mode success screen, but posting
  // to the member-facing upload route with the now-persisted member token
  // instead of adminToken.
  const [paynowReturnOrderId, setPaynowReturnOrderId] = useState(null);

  useEffect(() => {
    if (isPublic) return; // no auth required for the public signup flow
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isPublic, authLoading, user, isAdmin, navigate]);

  // Prefill referral code from ?ref= -- every existing referral
  // link/QR code app-wide (buildReferralLink) points at /signup?ref=CODE,
  // where CODE is the full "WELLIS-XXXX" code. The field itself only
  // holds the suffix now (the "WELLIS-" prefix is always shown, never
  // typed -- see the Step 1 input below), so strip it back off here,
  // case-insensitively, before prefilling.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) setReferralCode(ref.trim().replace(/^wellis-/i, ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect return from a PayNow Stripe Checkout redirect
  // (success_url: /signup?stripe=success&order_id=...). The account was
  // already created and its token persisted to localStorage before the
  // redirect (see handleSubmitPaynow), so this just needs to show the
  // proof-upload prompt for that order.
  useEffect(() => {
    if (searchParams.get('stripe') === 'success') {
      const orderId = searchParams.get('order_id');
      if (orderId) setPaynowReturnOrderId(orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The referral code input only ever holds the suffix a member types
  // (e.g. "COMPANY1") -- this is the actual "WELLIS-COMPANY1" sent to
  // the backend, which already matches case-insensitively
  // (validate_affiliate_code / create_email_user_with_referral_and_approve_tx
  // both normalize case -- migrations 137/185).
  const fullReferralCode = referralCode.trim() ? `WELLIS-${referralCode.trim()}` : '';

  // Debounced live referral validation (Step 1) -- same 500ms pattern as
  // the public Signup.jsx, now against the schema-fixed RPC (migration 184).
  useEffect(() => {
    if (!referralCode.trim()) {
      setAffiliateName('');
      setAffiliateRole('');
      setAffiliateError('');
      return;
    }
    setValidatingAffiliate(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/validate-affiliate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ affiliate_code: fullReferralCode }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setAffiliateName('');
          setAffiliateRole('');
          setAffiliateError(data.error || tt('Invalid referral code', '推荐码无效'));
        } else {
          setAffiliateName(data.affiliate_name || '');
          setAffiliateRole(data.affiliate_role || '');
          setAffiliateError('');
        }
      } catch (err) {
        setAffiliateName('');
        setAffiliateRole('');
        setAffiliateError(tt('Failed to validate referral code', '无法验证推荐码'));
      } finally {
        setValidatingAffiliate(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralCode]);

  // Auto-debounced Wellis ID uniqueness check (Step 2) -- fixes the
  // manual-button failure mode flagged in the wizard plan: fires on every
  // edit instead of requiring a click.
  useEffect(() => {
    if (wellisId.length !== 10) {
      setWellisIdAvailable(null);
      setWellisIdError('');
      return;
    }
    setCheckingWellisId(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/check-wellis-id`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wellis_id: `WELLIS-${wellisId.toUpperCase()}` }),
        });
        const data = await res.json();
        if (!res.ok) {
          setWellisIdAvailable(false);
          setWellisIdError(data.error || tt('Failed to check availability', '无法检查可用性'));
        } else if (data.available) {
          setWellisIdAvailable(true);
          setWellisIdError('');
        } else {
          setWellisIdAvailable(false);
          setWellisIdError(tt('This Wellis ID is already taken', '此 Wellis ID 已被占用'));
        }
      } catch (err) {
        setWellisIdAvailable(false);
        setWellisIdError(tt('Failed to check availability', '无法检查可用性'));
      } finally {
        setCheckingWellisId(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wellisId]);

  const loadStep4Data = useCallback(async () => {
    try {
      const packagesRes = await fetch(`${process.env.REACT_APP_API_URL}/api/packages`);
      if (packagesRes.ok) {
        const data = await packagesRes.json();
        setPackages(data.packages || []);
      }

      // Public mode: no admin session, no Credit/RnR balance of its own --
      // the only funding option is card payment.
      if (isPublic) return;

      const [balanceRes, rateRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/user/credit-balance`, { headers: { Authorization: `Bearer ${adminToken}` } }),
        fetch(`${process.env.REACT_APP_API_URL}/api/user/rnr-conversion-rate`, { headers: { Authorization: `Bearer ${adminToken}` } }),
      ]);
      if (balanceRes.ok) {
        const data = await balanceRes.json();
        setCreditBalance(data.credit_point_balance || 0);
        setRnrBalance(data.rnr_point_balance || 0);
      }
      if (rateRes.ok) {
        const data = await rateRes.json();
        setConversionRate(data.rate || 1);
      }
    } catch (err) {
      console.error('[SignupWizard] loadStep4Data error:', err);
    }
  }, [adminToken, isPublic]);

  useEffect(() => {
    if (step === 4) loadStep4Data();
  }, [step, loadStep4Data]);

  const passwordStrength = validatePasswordStrength(password);
  const passwordsMatch = !confirmPassword || confirmPassword === password;

  const selectedPackage = packages.find((p) => p.id === packageId);
  const price = selectedPackage ? Number(selectedPackage.price) : 0;
  const shortfall = Math.max(price - creditBalance, 0);
  const rnrNeeded = shortfall > 0 && conversionRate > 0 ? Math.ceil((shortfall / conversionRate) * 100) / 100 : 0;
  const combinedSpendingPower = creditBalance + rnrBalance * conversionRate;
  const insufficientBalance = !!selectedPackage && combinedSpendingPower < price;

  const canProceedStep1 = referralCode.trim() && affiliateName && !affiliateError && !validatingAffiliate;
  const canProceedStep2 = firstName.trim() && wellisId.length === 10 && wellisIdAvailable === true;
  const canProceedStep3 =
    (email.trim() || phone.trim()) &&
    password.length >= 8 &&
    passwordStrength !== 'weak' &&
    confirmPassword === password;
  const sponsorIsSalesTeam = affiliateRole === 'sales-team';
  const useRequestUpline = isPublic && sponsorIsSalesTeam && fundingMethod === 'request_upline';
  const usePaynow = isPublic && fundingMethod === 'paynow';
  const useStripe = isPublic ? !useRequestUpline && !usePaynow : fundingMethod === 'stripe';
  const canSubmit = !!packageId && !insufficientBalance && !submitting;
  const canSubmitStripe = !!packageId && !stripeSubmitting && (!isPublic || agreedToRefundPolicy);
  const canSubmitUplineRequest = !!packageId && !uplineRequestSubmitting && (!isPublic || agreedToRefundPolicy);
  const canSubmitPaynow = !!packageId && !paynowSubmitting && (!isPublic || agreedToRefundPolicy);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/user/signup-internal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          password,
          referral_code: fullReferralCode,
          wellis_id: wellisId.trim(),
          package_id: packageId,
          notes: notes.trim() || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || data.error || tt('Failed to create member', '创建会员失败'));
      }
      setSuccess({ wellisId: data.new_user_affiliate_code, packageOrderId: data.new_package_order_id });
    } catch (err) {
      setError(err.message || tt('Failed to create member', '创建会员失败'));
    } finally {
      setSubmitting(false);
    }
  };

  // Card-payment path -- used by both modes. Creates the account via the
  // same public signup endpoint /signup itself uses (auto-approved, no
  // email-verification gate -- a successful Stripe charge is treated as
  // proof), then either redirects the new member straight to their own
  // Stripe Checkout (public mode) or generates a payment link for the
  // admin to hand to the new member (internal mode, via
  // admin-create-stripe-checkout-for-user so the admin's own Credit/RnR
  // balance is never touched).
  const handleSubmitStripe = async () => {
    if (!packageId || stripeSubmitting) return;
    setStripeSubmitting(true);
    setError('');
    try {
      const signupRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          password,
          referral_code: fullReferralCode,
          wellis_id: wellisId.trim(),
        }),
      });
      const signupData = await signupRes.json();
      if (!signupRes.ok || !signupData.success) {
        throw new Error(signupData.error || tt('Failed to create account', '创建账户失败'));
      }

      if (isPublic) {
        // Persist the new session BEFORE navigating to Stripe -- when
        // Stripe redirects back to /packages, that page requires an
        // authenticated user (redirects to /login otherwise), and this
        // is a brand-new visitor with no prior localStorage token.
        localStorage.setItem('token', signupData.token);

        const checkoutRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/create-stripe-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${signupData.token}` },
          body: JSON.stringify({ package_id: packageId, source: 'signup_wizard' }),
        });
        const checkoutData = await checkoutRes.json();
        if (!checkoutRes.ok || !checkoutData.url) {
          throw new Error(checkoutData.error || tt('Failed to start payment', '无法开始付款'));
        }
        setStripeRedirect({ wellisId: signupData.affiliate_code, url: checkoutData.url });
      } else {
        const checkoutRes = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/create-stripe-checkout-for-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({ target_user_id: signupData.user_id, package_id: packageId }),
        });
        const checkoutData = await checkoutRes.json();
        if (!checkoutRes.ok || !checkoutData.url) {
          throw new Error(checkoutData.error || tt('Failed to generate payment link', '无法生成付款链接'));
        }
        setSuccess({ wellisId: signupData.affiliate_code, packageOrderId: checkoutData.order_id, stripeUrl: checkoutData.url });
      }
    } catch (err) {
      setError(err.message || tt('Failed to process card payment', '处理银行卡付款失败'));
    } finally {
      setStripeSubmitting(false);
    }
  };

  // PayNow path -- public mode, separate from card/USD Stripe. Creates the
  // account via the same public signup endpoint, persists the new session
  // (needed post-redirect, same as the card path), then creates an
  // SGD-priced Stripe Checkout Session with PayNow as the only payment
  // method and redirects there. Stripe's own success_url brings the member
  // back to /signup?stripe=success&order_id=... where the proof-upload
  // prompt picks up (see the paynowReturnOrderId effect above).
  const handleSubmitPaynow = async () => {
    if (!packageId || paynowSubmitting) return;
    setPaynowSubmitting(true);
    setError('');
    try {
      const signupRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          password,
          referral_code: fullReferralCode,
          wellis_id: wellisId.trim(),
        }),
      });
      const signupData = await signupRes.json();
      if (!signupRes.ok || !signupData.success) {
        throw new Error(signupData.error || tt('Failed to create account', '创建账户失败'));
      }

      // Same reasoning as handleSubmitStripe: persist the session before
      // navigating away, since Stripe redirects back to this same page.
      localStorage.setItem('token', signupData.token);

      const checkoutRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/create-stripe-checkout-paynow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${signupData.token}` },
        body: JSON.stringify({ package_id: packageId }),
      });
      const checkoutData = await checkoutRes.json();
      if (!checkoutRes.ok || !checkoutData.url) {
        throw new Error(checkoutData.error || tt('Failed to start PayNow payment', '无法开始 PayNow 付款'));
      }
      window.location.href = checkoutData.url;
    } catch (err) {
      setError(err.message || tt('Failed to process PayNow payment', '处理 PayNow 付款失败'));
      setPaynowSubmitting(false);
    }
  };

  // "Request from upline" path -- public mode, sponsor is a sales-team
  // member. Creates the account exactly like every other public signup
  // (pending/unverified, no package yet), then submits a package
  // funding request naming that sponsor. The sponsor (or an admin)
  // reviews it and, on approval, activates the chosen package funded
  // from their own Credit/RnR balance.
  const handleSubmitRequestUpline = async () => {
    if (!packageId || uplineRequestSubmitting) return;
    setUplineRequestSubmitting(true);
    setError('');
    try {
      const signupRes = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/email/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          password,
          referral_code: fullReferralCode,
          wellis_id: wellisId.trim(),
        }),
      });
      const signupData = await signupRes.json();
      if (!signupRes.ok || !signupData.success) {
        throw new Error(signupData.error || tt('Failed to create account', '创建账户失败'));
      }

      const requestRes = await fetch(`${process.env.REACT_APP_API_URL}/api/user/package-funding-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${signupData.token}` },
        body: JSON.stringify({ package_id: packageId, notes: notes.trim() || null }),
      });
      const requestData = await requestRes.json();
      if (!requestRes.ok || !requestData.success) {
        throw new Error(requestData.message || requestData.error || tt('Failed to submit request', '提交请求失败'));
      }

      setSuccess({ wellisId: signupData.affiliate_code, uplineRequestPending: true });
    } catch (err) {
      setError(err.message || tt('Failed to submit request', '提交请求失败'));
    } finally {
      setUplineRequestSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setReferralCode('');
    setAffiliateName('');
    setAffiliateError('');
    setFirstName('');
    setLastName('');
    setWellisId('');
    setWellisIdAvailable(null);
    setEmail('');
    setPhone('');
    setPassword('');
    setConfirmPassword('');
    setPackageId('');
    setNotes('');
    setSuccess(null);
    setStripeRedirect(null);
    setFundingMethod(isPublic ? 'stripe' : 'credit_rnr');
    setError('');
    setProofFiles([]);
    setProofUploaded([]);
    setProofError('');
    setPaynowReturnOrderId(null);
    setAgreedToRefundPolicy(false);
  };

  const handleProofFileChange = (e) => {
    setProofError('');
    const selected = Array.from(e.target.files || []);
    const remainingSlots = MAX_PROOF_PHOTOS - proofUploaded.length;
    if (selected.length > remainingSlots) {
      setProofError(tt(`You can add at most ${remainingSlots} more image(s).`, `最多还能添加 ${remainingSlots} 张图片。`));
      setProofFiles([]);
      return;
    }
    const oversized = selected.find((f) => f.size > MAX_PROOF_BYTES);
    if (oversized) {
      setProofError(tt('Each image must be 5MB or smaller.', '每张图片不能超过 5MB。'));
      setProofFiles([]);
      return;
    }
    const nonImage = selected.find((f) => !f.type.startsWith('image/'));
    if (nonImage) {
      setProofError(tt('Only image files are allowed.', '只能上传图片文件。'));
      setProofFiles([]);
      return;
    }
    setProofFiles(selected);
  };

  const handleUploadProof = async () => {
    // Two possible targets: the internal-mode success screen's order
    // (admin-uploaded, via adminToken) or, in public mode, the order the
    // member is returning to after a PayNow redirect (member-uploaded, via
    // their own freshly-persisted token).
    const orderId = paynowReturnOrderId || success?.packageOrderId;
    if (proofFiles.length === 0 || !orderId) return;
    setUploadingProof(true);
    setProofError('');
    try {
      const form = new FormData();
      form.append('order_id', orderId);
      proofFiles.forEach((f) => form.append('images', f));
      const uploadUrl = paynowReturnOrderId
        ? `${process.env.REACT_APP_API_URL}/api/user/package-order-attachments/upload`
        : `${process.env.REACT_APP_API_URL}/api/admin/package-order-attachments/upload`;
      const authToken = paynowReturnOrderId ? localStorage.getItem('token') : adminToken;
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || tt('Failed to upload images.', '图片上传失败。'));
      setProofUploaded((prev) => [...prev, ...proofFiles.map((f) => f.name)]);
      setProofFiles([]);
    } catch (err) {
      setProofError(err.message || tt('Failed to upload images.', '图片上传失败。'));
    } finally {
      setUploadingProof(false);
    }
  };

  if (!isPublic && (!user || !isAdmin)) return null;

  const cardStyle = { backgroundColor: '#0d2818', borderColor: '#c9a961' };
  const inputStyle = { backgroundColor: '#152214', borderColor: '#c9a961', color: '#b8a68f' };
  const stepLabels = isZh ? STEP_LABELS_ZH : STEP_LABELS_EN;

  const strengthColor = { weak: '#e05252', fair: '#d97706', good: '#c9a961', strong: '#4ade80' }[passwordStrength];

  if (paynowReturnOrderId) {
    return (
      <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
        <div className="max-w-2xl mx-auto">
          <div className="border rounded-lg p-6" style={cardStyle}>
            <p className="text-headline-sm font-bold mb-2" style={{ color: '#c9a961' }}>
              {tt('PayNow payment received', 'PayNow 付款已收到')}
            </p>
            <p className="text-body-sm mb-4" style={{ color: '#b8a68f' }}>
              {tt(
                'Your account has been created. An admin will review your payment and activate your package shortly. Uploading a screenshot of your PayNow receipt helps speed this up.',
                '您的账户已创建。管理员将很快审核您的付款并激活您的套餐。上传 PayNow 收据截图有助于加快审核。'
              )}
            </p>
            {proofError && <p className="text-body-xs mb-2" style={{ color: '#e05252' }}>{proofError}</p>}
            {proofUploaded.length > 0 && (
              <p className="text-body-xs mb-2" style={{ color: '#c9a961' }}>
                ✓ {tt(`${proofUploaded.length} image(s) uploaded`, `已上传 ${proofUploaded.length} 张图片`)}
              </p>
            )}
            {proofUploaded.length < MAX_PROOF_PHOTOS && (
              <>
                <input type="file" accept="image/*" multiple onChange={handleProofFileChange} disabled={uploadingProof} />
                {proofFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={handleUploadProof}
                    disabled={uploadingProof}
                    className="mt-2 w-full px-4 py-2 rounded-lg font-medium disabled:opacity-50 text-black"
                    style={{ backgroundColor: '#c9a961' }}
                  >
                    {uploadingProof ? tt('Uploading...', '上传中...') : tt(`Upload ${proofFiles.length} image(s)`, `上传 ${proofFiles.length} 张图片`)}
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-4 px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {tt('Go to Dashboard', '前往仪表板')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-headline-lg font-bold mb-2" style={{ color: '#c9a961' }}>
          {isPublic ? tt('Join Wellis', '加入 Wellis') : tt('Signup Wizard (Admin)', '注册向导（管理员）')}
        </h1>
        <p className="text-body-sm mb-6" style={{ color: '#999999' }}>
          {isPublic
            ? tt('Create your account and activate your package.', '创建您的账户并激活您的套餐。')
            : tt(
                'Guided member creation, funded from your own Credit Points balance or a card-payment link.',
                '引导式会员创建，可从您自己的认购积分余额扣除，或生成银行卡付款链接。'
              )}
        </p>

        {!isPublic && (
          <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
            {[
              ['wizard', tt('Run Wizard', '运行向导')],
              ['share', tt('Share My Link', '分享我的链接')],
            ].map(([val, label]) => (
              <button
                key={val}
                type="button"
                onClick={() => setActiveTab(val)}
                className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
                style={activeTab === val ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {!isPublic && activeTab === 'share' ? (
          <div className="border rounded-lg p-6" style={cardStyle}>
            <p className="text-body-sm mb-4" style={{ color: '#b8a68f' }}>
              {tt(
                'Share this link (or QR code) with a prospect -- they can self-register on the public signup page with your referral code pre-filled.',
                '将此链接（或二维码）分享给潜在客户——他们可以在公开注册页面自助注册，并自动填入您的推荐码。'
              )}
            </p>
            {user?.wellis_id || user?.affiliate_code ? (
              <>
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    readOnly
                    value={buildReferralLink(user.wellis_id || user.affiliate_code)}
                    className="flex-1 p-3 rounded border text-body-sm"
                    style={{ backgroundColor: '#152214', borderColor: '#3d5a4a', color: '#b8a68f' }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await copyToClipboard(buildReferralLink(user.wellis_id || user.affiliate_code));
                      setCopyStatus(ok ? tt('Copied!', '已复制！') : tt('Failed to copy', '复制失败'));
                      setTimeout(() => setCopyStatus(''), 2000);
                    }}
                    className="px-4 py-2 rounded-lg font-medium whitespace-nowrap"
                    style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                  >
                    {tt('Copy', '复制')}
                  </button>
                </div>
                {copyStatus && <p className="text-body-xs mb-4" style={{ color: '#c9a961' }}>{copyStatus}</p>}
                <div className="flex flex-col items-center p-6 rounded-lg" style={{ backgroundColor: '#ffffff' }}>
                  <QRCodeSVG
                    value={buildReferralLink(user.wellis_id || user.affiliate_code)}
                    size={200}
                    level="H"
                    title={tt('Referral QR code', '推荐二维码')}
                  />
                </div>
              </>
            ) : (
              <p className="text-body-sm" style={{ color: '#e05252' }}>{tt('Your account has no Wellis ID.', '您的账户没有 Wellis ID。')}</p>
            )}
          </div>
        ) : stripeRedirect ? (
          <div className="border rounded-lg p-6" style={cardStyle}>
            <p className="text-headline-sm font-bold mb-2" style={{ color: '#c9a961' }}>
              {tt('Account created!', '账户创建成功！')}
            </p>
            <p className="mb-4" style={{ color: '#b8a68f' }}>
              {tt('Your Wellis ID', '您的 Wellis ID')}: <span className="font-mono font-bold" style={{ color: '#c9a961' }}>{stripeRedirect.wellisId}</span>
            </p>
            <p className="text-body-xs mb-4" style={{ color: '#e05252' }}>
              {tt(
                'Please save this Wellis ID before continuing to payment -- it identifies your account in case of any payment disputes.',
                '请在继续付款前保存此 Wellis ID——如发生付款争议，将用于识别您的账户。'
              )}
            </p>
            <button
              onClick={() => { window.location.href = stripeRedirect.url; }}
              className="w-full px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {tt('Continue to Payment', '继续付款')}
            </button>
          </div>
        ) : success ? (
          <div className="border rounded-lg p-6" style={cardStyle}>
            <p className="text-headline-sm font-bold mb-2" style={{ color: '#c9a961' }}>
              {tt('Member created successfully', '会员创建成功')}
            </p>
            <p style={{ color: '#b8a68f' }}>
              {tt('Wellis ID', 'Wellis ID')}: <span className="font-mono font-bold" style={{ color: '#c9a961' }}>{success.wellisId}</span>
            </p>

            {success.uplineRequestPending && (
              <div className="mt-6 pt-4 border-t" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                  {tt(
                    'Your request has been sent to your sponsor. Your package will be activated once they approve it.',
                    '您的请求已发送给您的推荐人。批准后，您的套餐将被激活。'
                  )}
                </p>
              </div>
            )}

            {success.stripeUrl && (
              <div className="mt-6 pt-4 border-t text-center" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                <p className="text-body-sm font-medium mb-3" style={{ color: '#c9a961' }}>
                  {tt('Card Payment Link', '银行卡付款链接')}
                </p>
                <div className="inline-block p-3 rounded bg-white mb-3">
                  <QRCodeSVG value={success.stripeUrl} size={160} />
                </div>
                <div className="flex gap-2">
                  <input readOnly value={success.stripeUrl} className="flex-1 p-2 rounded border text-body-xs" style={inputStyle} />
                  <button
                    type="button"
                    onClick={async () => { await copyToClipboard(success.stripeUrl); setCopyStatus(tt('Copied!', '已复制！')); setTimeout(() => setCopyStatus(''), 2000); }}
                    className="px-3 py-2 rounded font-medium text-body-xs text-black"
                    style={{ backgroundColor: '#c9a961' }}
                  >
                    {copyStatus || tt('Copy', '复制')}
                  </button>
                </div>
                <p className="text-body-xs mt-2" style={{ color: '#999999' }}>
                  {tt('Share this with the new member, or open it here if they are paying in person.', '将此链接分享给新会员，或如现场付款可直接在此打开。')}
                </p>
              </div>
            )}

            {success.packageOrderId && !success.stripeUrl && (
              <div className="mt-6 pt-4 border-t" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                <p className="text-body-sm font-medium mb-1" style={{ color: '#c9a961' }}>
                  {tt('Payment Proof (optional)', '付款凭证（可选）')}
                </p>
                <p className="text-body-xs mb-3" style={{ color: '#999999' }}>
                  {tt(
                    'Up to 5 images, 5MB max each. Shows up in Reports > All Transactions for this order.',
                    '最多 5 张图片，每张不超过 5MB。将显示在报告 > 全部交易中该订单下。'
                  )}
                </p>
                {proofError && <p className="text-body-xs mb-2" style={{ color: '#e05252' }}>{proofError}</p>}
                {proofUploaded.length > 0 && (
                  <p className="text-body-xs mb-2" style={{ color: '#c9a961' }}>
                    ✓ {tt(`${proofUploaded.length} image(s) uploaded`, `已上传 ${proofUploaded.length} 张图片`)}
                  </p>
                )}
                {proofUploaded.length < MAX_PROOF_PHOTOS && (
                  <>
                    <input type="file" accept="image/*" multiple onChange={handleProofFileChange} disabled={uploadingProof} />
                    {proofFiles.length > 0 && (
                      <button
                        type="button"
                        onClick={handleUploadProof}
                        disabled={uploadingProof}
                        className="mt-2 w-full px-4 py-2 rounded-lg font-medium disabled:opacity-50 text-black"
                        style={{ backgroundColor: '#c9a961' }}
                      >
                        {uploadingProof ? tt('Uploading...', '上传中...') : tt(`Upload ${proofFiles.length} image(s)`, `上传 ${proofFiles.length} 张图片`)}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            <button
              onClick={resetAll}
              className="w-full mt-4 px-4 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity text-black"
              style={{ backgroundColor: '#c9a961' }}
            >
              {tt('Sign up another member', '注册另一位会员')}
            </button>
          </div>
        ) : (
          <div className="border rounded-lg p-6" style={cardStyle}>
            <WizardProgress steps={stepLabels} currentStep={step} />

            {error && (
              <div className="p-3 mb-4 rounded border text-body-sm" style={{ backgroundColor: 'rgba(224,82,82,0.1)', borderColor: '#e05252', color: '#e05252' }}>
                {error}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Sponsor Referral Code', '推荐人代码')}</label>
                  <div className="flex rounded border overflow-hidden" style={{ borderColor: inputStyle.borderColor }}>
                    <span
                      className="px-3 py-3 text-body-sm font-medium select-none"
                      style={{ backgroundColor: '#0d2818', color: '#999999' }}
                    >
                      WELLIS-
                    </span>
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.replace(/^wellis-/i, ''))}
                      placeholder={tt('e.g. COMPANY1', '例如 COMPANY1')}
                      className="flex-1 min-w-0 p-3 border-0"
                      style={{ backgroundColor: inputStyle.backgroundColor, color: inputStyle.color }}
                    />
                  </div>
                  {validatingAffiliate && <p className="text-body-xs mt-1" style={{ color: '#999999' }}>{tt('Checking...', '检查中...')}</p>}
                  {affiliateName && !validatingAffiliate && (
                    <p className="text-body-xs mt-1" style={{ color: '#c9a961' }}>✓ {tt('Sponsor', '推荐人')}: {affiliateName}</p>
                  )}
                  {affiliateError && <p className="text-body-xs mt-1" style={{ color: '#e05252' }}>{affiliateError}</p>}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('First Name', '名字')}</label>
                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full p-3 rounded border" style={inputStyle} />
                  </div>
                  <div>
                    <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Last Name', '姓氏')}</label>
                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full p-3 rounded border" style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt("Member's Wellis ID", '会员的 Wellis ID')}</label>
                  <div className="flex items-center rounded border overflow-hidden" style={inputStyle}>
                    <span className="pl-3 text-body-sm" style={{ color: '#999999' }}>WELLIS-</span>
                    <input
                      type="text"
                      value={wellisId}
                      maxLength={10}
                      onChange={(e) => setWellisId(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      className="w-full p-3 bg-transparent focus:outline-none"
                      style={{ color: '#b8a68f' }}
                    />
                  </div>
                  <p className="text-body-xs mt-1" style={{ color: '#999999' }}>{tt('10 alphanumeric characters', '10 个字母数字字符')}</p>
                  {checkingWellisId && <p className="text-body-xs mt-1" style={{ color: '#999999' }}>{tt('Checking...', '检查中...')}</p>}
                  {wellisIdAvailable === true && <p className="text-body-xs mt-1" style={{ color: '#c9a961' }}>✓ {tt('Available', '可用')}</p>}
                  {wellisIdError && <p className="text-body-xs mt-1" style={{ color: '#e05252' }}>{wellisIdError}</p>}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Email', '电子邮件')}</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 rounded border" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Phone (optional if email provided)', '电话（如已提供电子邮件则可选）')}</label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full p-3 rounded border" style={inputStyle} />
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Password', '密码')}</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full p-3 pr-16 rounded border"
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-body-xs"
                      style={{ color: '#c9a961' }}
                    >
                      {showPassword ? tt('Hide', '隐藏') : tt('Show', '显示')}
                    </button>
                  </div>
                  {password && (
                    <p className="text-body-xs mt-1" style={{ color: strengthColor }}>
                      {tt('Strength', '强度')}: {tt(passwordStrength, { weak: '弱', fair: '一般', good: '良好', strong: '强' }[passwordStrength])}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Confirm Password', '确认密码')}</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full p-3 pr-16 rounded border"
                      style={{ ...inputStyle, borderColor: passwordsMatch ? '#c9a961' : '#e05252' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-body-xs"
                      style={{ color: '#c9a961' }}
                    >
                      {showConfirmPassword ? tt('Hide', '隐藏') : tt('Show', '显示')}
                    </button>
                  </div>
                  {!passwordsMatch && <p className="text-body-xs mt-1" style={{ color: '#e05252' }}>{tt('Passwords do not match', '密码不匹配')}</p>}
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                {!isPublic && (
                  <div className="p-3 rounded border text-center" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
                    <div className="text-body-xs" style={{ color: '#999999' }}>{tt('Your Credit Points', '您的认购积分')}</div>
                    <div className="font-bold" style={{ color: '#c9a961' }}>{creditBalance.toFixed(2)}</div>
                  </div>
                )}

                {!isPublic && (
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['credit_rnr', tt('Fund with Credit/RnR', '用认购积分/RnR 资助')],
                      ['stripe', tt('Card Payment Link', '银行卡付款链接')],
                    ].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setFundingMethod(val)}
                        className="px-3 py-2 rounded-md border text-body-sm font-medium transition"
                        style={fundingMethod === val
                          ? { backgroundColor: '#c9a961', color: '#0d2818', borderColor: '#c9a961' }
                          : { backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {isPublic && (
                  <div className={sponsorIsSalesTeam ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 gap-2'}>
                    {[
                      ['stripe', tt('Pay with Card', '银行卡付款')],
                      ['paynow', tt('PayNow', 'PayNow')],
                      ...(sponsorIsSalesTeam ? [['request_upline', tt('Request from Upline', '向上级申请')]] : []),
                    ].map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setFundingMethod(val)}
                        className="px-3 py-2 rounded-md border text-body-sm font-medium transition"
                        style={fundingMethod === val
                          ? { backgroundColor: '#c9a961', color: '#0d2818', borderColor: '#c9a961' }
                          : { backgroundColor: 'transparent', color: '#b8a68f', borderColor: '#3d5a4a' }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                <div>
                  <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Package', '套餐')}</label>
                  <select value={packageId} onChange={(e) => setPackageId(e.target.value)} className="w-full p-3 rounded border" style={inputStyle}>
                    <option value="">{tt('Select a package...', '选择套餐...')}</option>
                    {packages.map((p) => (
                      <option key={p.id} value={p.id}>{p.name_en || p.code} — {Number(p.price).toFixed(0)} {tt('Credit Points', '认购积分')}</option>
                    ))}
                  </select>
                </div>

                {useRequestUpline ? (
                  selectedPackage && (
                    <p className="text-body-xs" style={{ color: '#999999' }}>
                      {tt(
                        `Your account will be created now. Your sponsor (${affiliateName}) will be asked to fund and activate the ${selectedPackage.name_en || selectedPackage.code} package for you.`,
                        `您的账户将立即创建。您的推荐人（${affiliateName}）将被要求为您资助并激活 ${selectedPackage.name_cn || selectedPackage.code} 套餐。`
                      )}
                    </p>
                  )
                ) : usePaynow ? (
                  selectedPackage && (
                    <p className="text-body-xs" style={{ color: '#999999' }}>
                      {tt(
                        `You'll be charged the PayNow (SGD) equivalent of ${Number(selectedPackage.price).toFixed(0)} Credit Points on the next screen.`,
                        `您将在下一屏幕被收取相当于 ${Number(selectedPackage.price).toFixed(0)} 认购积分的 PayNow（新元）付款。`
                      )}
                    </p>
                  )
                ) : useStripe ? (
                  selectedPackage && (
                    <p className="text-body-xs" style={{ color: '#999999' }}>
                      {tt(
                        `You'll be charged the card equivalent of ${Number(selectedPackage.price).toFixed(0)} Credit Points on the next screen.`,
                        `您将在下一屏幕被收取相当于 ${Number(selectedPackage.price).toFixed(0)} 认购积分的银行卡付款。`
                      )}
                    </p>
                  )
                ) : (
                  <>
                    {selectedPackage && rnrNeeded > 0 && (
                      <p className="text-body-xs" style={{ color: '#c9a961' }}>
                        {tt(
                          'Additional balance will be applied automatically to cover the shortfall.',
                          '将自动补足差额。'
                        )}
                      </p>
                    )}
                    {insufficientBalance && (
                      <p className="text-body-xs" style={{ color: '#e05252' }}>
                        {tt('Insufficient balance for this package.', '余额不足以支付此套餐。')}
                      </p>
                    )}
                  </>
                )}

                {(!isPublic || useRequestUpline) && (
                  <div>
                    <label className="block text-body-sm mb-2" style={{ color: '#b8a68f' }}>{tt('Notes (optional)', '备注（可选）')}</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" className="w-full p-3 rounded border" style={inputStyle} />
                  </div>
                )}

                {isPublic && selectedPackage && (
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreedToRefundPolicy}
                      onChange={(e) => setAgreedToRefundPolicy(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span className="text-body-xs" style={{ color: agreedToRefundPolicy ? '#999999' : '#e05252' }}>
                      {tt(
                        'I understand that funds paid are subject to a 50% administrative charge if refunded. ',
                        '我理解已支付款项如需退款，将收取 50% 的行政费用。'
                      )}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#c9a961' }}>
                        {tt('View Terms & Policy', '查看条款与政策')}
                      </Link>
                    </span>
                  </label>
                )}
              </div>
            )}

            <div className="flex gap-3 mt-6 pt-4 border-t" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => s - 1)}
                  className="flex-1 px-4 py-3 rounded-lg font-medium border"
                  style={{ borderColor: '#3d5a4a', color: '#b8a68f' }}
                >
                  {tt('Back', '上一步')}
                </button>
              )}
              {step < 4 && (
                <button
                  type="button"
                  onClick={() => setStep((s) => s + 1)}
                  disabled={(step === 1 && !canProceedStep1) || (step === 2 && !canProceedStep2) || (step === 3 && !canProceedStep3)}
                  className="flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {tt('Next', '下一步')}
                </button>
              )}
              {step === 4 && useRequestUpline && (
                <button
                  type="button"
                  onClick={handleSubmitRequestUpline}
                  disabled={!canSubmitUplineRequest}
                  className="flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {uplineRequestSubmitting
                    ? tt('Submitting...', '提交中...')
                    : tt('Create Account & Request from Upline', '创建账户并向上级申请')}
                </button>
              )}
              {step === 4 && useStripe && (
                <button
                  type="button"
                  onClick={handleSubmitStripe}
                  disabled={!canSubmitStripe}
                  className="flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {stripeSubmitting
                    ? tt('Processing...', '处理中...')
                    : isPublic
                      ? tt('Create Account & Pay with Card', '创建账户并用银行卡付款')
                      : tt('Create Member & Generate Payment Link', '创建会员并生成付款链接')}
                </button>
              )}
              {step === 4 && usePaynow && (
                <button
                  type="button"
                  onClick={handleSubmitPaynow}
                  disabled={!canSubmitPaynow}
                  className="flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {paynowSubmitting
                    ? tt('Processing...', '处理中...')
                    : tt('Create Account & Pay with PayNow', '创建账户并使用 PayNow 付款')}
                </button>
              )}
              {step === 4 && !useStripe && !useRequestUpline && !usePaynow && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="flex-1 px-4 py-3 rounded-lg font-medium disabled:opacity-50 text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {submitting ? tt('Creating...', '创建中...') : tt('Create Member & Activate Package', '创建会员并激活套餐')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
